import React, { useState, useContext } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Card,
  Button,
  FluentProvider,
  Persona,
  webLightTheme,
  Text,
  Caption1,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  DeleteRegular,
  Warning24Regular,
  OpenRegular,
  DocumentRegular,
  DocumentPdfRegular,
  DocumentTableRegular,
  MailRegular,
  TableAltTextRegular,
  DocumentImageRegular,
  DocumentTextRegular,
  DocumentBulletListRegular,
} from '@fluentui/react-icons';
import getIconName from './fileSelector.config';
import { IFileValidationError } from '../../../../utils/validation/validateFiles';
import { StatUser } from '../../../../utils/statApi/UsersApi';
import { openBlob, downloadBlob } from '../../documents/documentDownload';
import { MessagingColors } from '../../../../app/common/styles/CommonStyleObjects';
import hasAccess from '../../../../utils/authorization/authorizationCheck';
import UserContext from '../../../../utils/authorization/UserContext';

interface IFileSelectorItemProps {
  index: number;
  user: StatUser;
  fileName: string;
  url?: string;
  documentId?: number;
  validationErrors?: IFileValidationError[];
  onRemoveFromSelected?: (index: number) => void;
  allowDownload?: boolean;
  allowViewInTab?: boolean;
  allowDelete?: boolean;
  uiDisabled?: boolean;
  isMilestoneComplete?: boolean;
  activity: string;
}

const useStyles = makeStyles({
  iconError: {
    fontSize: '32px',
    color: MessagingColors.errorBlockIcon,
  },
  iconSuccess: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
});

const FileSelectorItem: React.FC<IFileSelectorItemProps> = (props: IFileSelectorItemProps) => {
  const {
    index,
    user,
    fileName,
    url,
    documentId = 0,
    onRemoveFromSelected,
    allowDownload,
    allowViewInTab,
    allowDelete,
    uiDisabled = false,
    isMilestoneComplete,
    validationErrors,
    activity,
  } = props;

  const loggedInUser: StatUser = useContext(UserContext);
  const { permissions } = loggedInUser;
  const [showWarnBeforeDelete, setShowWarnBeforeDelete] = useState(false);
  const [hasDownloadError, setHasDownloadError] = useState(false);
  const styles = useStyles();

  const hasErrors = (validationErrors && validationErrors.length > 0) || hasDownloadError;

  const getUploadErrors = (): string =>
    hasDownloadError
      ? 'Error when downloading file'
      : (validationErrors || []).map((e) => e.title).join(' ');

  const warnBeforeDelete = (): JSX.Element => {
    return (
      <Stack horizontalAlign="center" verticalAlign="center">
        <p>Are you sure you want to remove this file</p>
        <Stack horizontalAlign="center" horizontal tokens={{ childrenGap: 20 }}>
          <Button
            appearance="primary"
            onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
              event.stopPropagation();
              if (onRemoveFromSelected) {
                onRemoveFromSelected(index);
              }
              setShowWarnBeforeDelete(false);
            }}
          >
            Yes
          </Button>
          <Button
            onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
              event.stopPropagation();
              setShowWarnBeforeDelete(false);
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    );
  };

  const onReadError = (): void => {
    setHasDownloadError(true);
  };

  const getFileTypeIcon = (type: string): JSX.Element => {
    const iconClassName = hasErrors ? styles.iconError : styles.iconSuccess;

    switch (type) {
      case 'WordLogo':
        return <DocumentBulletListRegular className={iconClassName} />;
      case 'TextDocument':
        return <DocumentTextRegular className={iconClassName} />;
      case 'PowerPointLogo':
        return <TableAltTextRegular className={iconClassName} />;
      case 'PDF':
        return <DocumentPdfRegular className={iconClassName} />;
      case 'ExcelLogo':
        return <DocumentTableRegular className={iconClassName} />;
      case 'OutlookLogo':
        return <MailRegular className={iconClassName} />;
      case 'FileImage':
        return <DocumentImageRegular className={iconClassName} />;
      case 'Warning':
        return <Warning24Regular className={iconClassName} />;
      default:
        return <DocumentRegular className={iconClassName} />;
    }
  };

  const iconName = hasErrors ? 'Warning' : getIconName(fileName);
  const canView = allowViewInTab && hasAccess(permissions, 'MilestoneDocument:Download');
  const canDownload = allowDownload && hasAccess(permissions, 'MilestoneDocument:Download');
  const canDelete =
    (allowDelete ?? true) &&
    !!onRemoveFromSelected &&
    hasAccess(permissions, 'MilestoneDocument:Remove');

  return (
    <FluentProvider theme={webLightTheme}>
      <Card orientation="horizontal" aria-label="File selected to upload for milestone signoff">
        <div>{getFileTypeIcon(iconName)}</div>
        {!showWarnBeforeDelete && (
          <>
            <div>
              <Text weight="semibold">{fileName}</Text>
              {!hasErrors && !uiDisabled && canView && url && (
                <div>
                  <Button
                    appearance="transparent"
                    icon={<OpenRegular />}
                    disabled={documentId <= 0}
                    onClick={() => openBlob(documentId, fileName, onReadError)}
                    aria-label={`View Document: ${fileName}`}
                  >
                    View Document
                  </Button>
                </div>
              )}

              {hasErrors && (
                <Caption1>
                  <span role="alert">
                    <Warning24Regular /> {getUploadErrors()}
                  </span>
                </Caption1>
              )}

              {!hasErrors && (
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                  <Persona
                    name={user?.displayName}
                    avatar={{
                      size: 24,
                      color: 'colorful',
                      initials: `${user?.firstName?.charAt(0) || ''}${
                        user?.lastName?.charAt(0) || ''
                      }`,
                    }}
                    size="extra-small"
                  />
                  <Caption1>{activity}</Caption1>
                </Stack>
              )}
            </div>

            <Stack horizontal tokens={{ childrenGap: 4 }}>
              {!uiDisabled && canDownload && url && (
                <Button
                  appearance="transparent"
                  icon={<ArrowDownloadRegular />}
                  disabled={documentId <= 0}
                  onClick={() => downloadBlob(documentId, fileName, onReadError)}
                  aria-label={`Download file: ${fileName}`}
                />
              )}
              {!uiDisabled && !isMilestoneComplete && canDelete && (
                <Button
                  appearance="transparent"
                  icon={<DeleteRegular />}
                  onClick={() => setShowWarnBeforeDelete(true)}
                  aria-label={`Remove file: ${fileName} from uploads`}
                />
              )}
            </Stack>
          </>
        )}
        {showWarnBeforeDelete && warnBeforeDelete()}
      </Card>
    </FluentProvider>
  );
};

export default FileSelectorItem;
